/**
 * Comprehensive list of world currencies for travel applications
 * Sorted alphabetically by currency code
 * 
 * Usage:
 *   import { CURRENCIES, CURRENCY_CODES, getCurrencyByCode } from '@/utils/currencies';
 */

// Full currency data with code, name, and symbol
export const CURRENCIES = [
  { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
  { code: "ARS", name: "Argentine Peso", symbol: "$" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "BDT", name: "Bangladeshi Taka", symbol: "৳" },
  { code: "BGN", name: "Bulgarian Lev", symbol: "лв" },
  { code: "BHD", name: "Bahraini Dinar", symbol: ".د.ب" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "CHF", name: "Swiss Franc", symbol: "CHF" },
  { code: "CLP", name: "Chilean Peso", symbol: "$" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "COP", name: "Colombian Peso", symbol: "$" },
  { code: "CZK", name: "Czech Koruna", symbol: "Kč" },
  { code: "DKK", name: "Danish Krone", symbol: "kr" },
  { code: "EGP", name: "Egyptian Pound", symbol: "£" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
  { code: "HRK", name: "Croatian Kuna", symbol: "kn" },
  { code: "HUF", name: "Hungarian Forint", symbol: "Ft" },
  { code: "IDR", name: "Indonesian Rupiah", symbol: "Rp" },
  { code: "ILS", name: "Israeli Shekel", symbol: "₪" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "ISK", name: "Icelandic Króna", symbol: "kr" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "KES", name: "Kenyan Shilling", symbol: "KSh" },
  { code: "KRW", name: "South Korean Won", symbol: "₩" },
  { code: "KWD", name: "Kuwaiti Dinar", symbol: "د.ك" },
  { code: "LKR", name: "Sri Lankan Rupee", symbol: "Rs" },
  { code: "MAD", name: "Moroccan Dirham", symbol: "د.م." },
  { code: "MXN", name: "Mexican Peso", symbol: "MX$" },
  { code: "MYR", name: "Malaysian Ringgit", symbol: "RM" },
  { code: "NGN", name: "Nigerian Naira", symbol: "₦" },
  { code: "NOK", name: "Norwegian Krone", symbol: "kr" },
  { code: "NZD", name: "New Zealand Dollar", symbol: "NZ$" },
  { code: "OMR", name: "Omani Rial", symbol: "ر.ع." },
  { code: "PEN", name: "Peruvian Sol", symbol: "S/" },
  { code: "PHP", name: "Philippine Peso", symbol: "₱" },
  { code: "PKR", name: "Pakistani Rupee", symbol: "Rs" },
  { code: "PLN", name: "Polish Zloty", symbol: "zł" },
  { code: "QAR", name: "Qatari Riyal", symbol: "ر.ق" },
  { code: "RON", name: "Romanian Leu", symbol: "lei" },
  { code: "RUB", name: "Russian Ruble", symbol: "₽" },
  { code: "SAR", name: "Saudi Riyal", symbol: "ر.س" },
  { code: "SEK", name: "Swedish Krona", symbol: "kr" },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
  { code: "THB", name: "Thai Baht", symbol: "฿" },
  { code: "TRY", name: "Turkish Lira", symbol: "₺" },
  { code: "TWD", name: "Taiwan Dollar", symbol: "NT$" },
  { code: "UAH", name: "Ukrainian Hryvnia", symbol: "₴" },
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "VND", name: "Vietnamese Dong", symbol: "₫" },
  { code: "ZAR", name: "South African Rand", symbol: "R" },
].sort((a, b) => a.code.localeCompare(b.code));

// Simple array of currency codes (for backwards compatibility)
export const CURRENCY_CODES = CURRENCIES.map(c => c.code);

// Get currency details by code
export const getCurrencyByCode = (code) => {
  return CURRENCIES.find(c => c.code === code) || null;
};

// Get currency symbol by code
export const getCurrencySymbol = (code) => {
  const currency = getCurrencyByCode(code);
  return currency ? currency.symbol : code;
};

// Format currency for display in dropdowns (e.g., "USD - US Dollar")
export const formatCurrencyOption = (currency) => {
  return `${currency.code} - ${currency.name}`;
};

// Common currencies for quick access (most frequently used in travel)
export const COMMON_CURRENCIES = [
  "USD", "EUR", "GBP", "JPY", "CAD", "AUD", "CHF", "CNY", 
  "SGD", "HKD", "THB", "VND", "KRW", "INR", "MXN"
];

// Country → currency code mapping for all currencies in CURRENCIES above.
// Used by segment/payment forms to suggest relevant currencies by country.
// Only primary currencies; territories/dependents map to the sovereign country.
export const COUNTRY_CURRENCY = {
  // Americas
  "United States": "USD", "Canada": "CAD", "Mexico": "MXN",
  "Brazil": "BRL", "Argentina": "ARS", "Chile": "CLP", "Colombia": "COP", "Peru": "PEN",
  // Europe (Eurozone)
  "Austria": "EUR", "Belgium": "EUR", "Croatia": "EUR", "Cyprus": "EUR", "Estonia": "EUR",
  "Finland": "EUR", "France": "EUR", "Germany": "EUR", "Greece": "EUR", "Ireland": "EUR",
  "Italy": "EUR", "Latvia": "EUR", "Lithuania": "EUR", "Luxembourg": "EUR", "Malta": "EUR",
  "Netherlands": "EUR", "Portugal": "EUR", "Slovakia": "EUR", "Slovenia": "EUR", "Spain": "EUR",
  "Andorra": "EUR", "Monaco": "EUR", "San Marino": "EUR", "Vatican City": "EUR",
  "Montenegro": "EUR", "Kosovo": "EUR",
  // Europe (non-Euro)
  "United Kingdom": "GBP", "Switzerland": "CHF", "Norway": "NOK", "Sweden": "SEK",
  "Denmark": "DKK", "Iceland": "ISK", "Poland": "PLN", "Czech Republic": "CZK",
  "Hungary": "HUF", "Bulgaria": "BGN", "Romania": "RON", "Ukraine": "UAH", "Russia": "RUB",
  "Turkey": "TRY",
  // Asia
  "Japan": "JPY", "China": "CNY", "South Korea": "KRW", "Taiwan": "TWD",
  "Hong Kong": "HKD", "Singapore": "SGD", "Thailand": "THB", "Vietnam": "VND",
  "Malaysia": "MYR", "Indonesia": "IDR", "Philippines": "PHP", "India": "INR",
  "Pakistan": "PKR", "Bangladesh": "BDT", "Sri Lanka": "LKR",
  // Oceania
  "Australia": "AUD", "New Zealand": "NZD",
  // Middle East
  "United Arab Emirates": "AED", "Saudi Arabia": "SAR", "Qatar": "QAR",
  "Kuwait": "KWD", "Bahrain": "BHD", "Oman": "OMR", "Israel": "ILS",
  // Africa
  "South Africa": "ZAR", "Egypt": "EGP", "Morocco": "MAD", "Kenya": "KES", "Nigeria": "NGN",
};

/**
 * Get the primary currency code for a country name.
 * Returns null if unknown.
 */
export const getCountryCurrency = (countryName) => {
  if (!countryName) return null;
  return COUNTRY_CURRENCY[countryName] || null;
};

/**
 * Get the user's configured home currency from localStorage.
 * Falls back to "USD" when missing/unparseable.
 */
export const getHomeCurrency = () => {
  try {
    const raw = localStorage.getItem('userSettings');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.home_currency) return parsed.home_currency;
    }
  } catch {
    // ignore
  }
  return 'USD';
};

// Check if a currency code is valid
export const isValidCurrency = (code) => {
  return CURRENCY_CODES.includes(code) || code === 'POINTS';
};

// Currency options formatted for Select dropdowns (value/label pairs)
// Includes POINTS for travel points/miles
export const CURRENCY_OPTIONS = [
  ...CURRENCIES.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` })),
  { value: 'POINTS', label: 'POINTS - Travel Points/Miles' }
].sort((a, b) => a.value.localeCompare(b.value));
